/**
 * PlanetPanel — top-level overlay container for the planet management
 * panel. Mounted unconditionally in App.tsx; renders only when the
 * Zustand store reports `isOpen`.
 *
 * The hook `usePlanetPanel()` wires all server-side broadcasts into the
 * store and drives the open/close handshake.
 */

import { useEffect } from 'react';
import { usePlanetPanel } from './hooks/usePlanetPanel';
import { usePlanetPanelStore } from './state/planetPanelStore';
import { PlanetGlobe } from './PlanetGlobe';
import { PlanetInfoPanel } from './PlanetInfoPanel';
import { PlanetLayerControls } from './PlanetLayerControls';
import { PlanetResourceLegend } from './PlanetResourceLegend';
import { PlanetCenterOutpost } from './PlanetCenterOutpost';
import { networkManager } from '../../systems/NetworkManager';
import { emitGameEvent } from '../../core/GameEvents';
import { authService } from '../../services/SupabaseClient';
import ScaleLoader from 'react-spinners/ScaleLoader';

export function PlanetPanel() {
  // Wire all broadcasts → store. Always-on (cheap).
  usePlanetPanel();

  const isOpen = usePlanetPanelStore((s) => s.isOpen);
  const isLoading = usePlanetPanelStore((s) => s.isLoading);
  const lastError = usePlanetPanelStore((s) => s.lastError);
  const isBaking = usePlanetPanelStore((s) => s.isBakingTexture);
  const resourcesOn = usePlanetPanelStore((s) => s.layers.noise);
  const setSelfPlayerId = usePlanetPanelStore((s) => s.setSelfPlayerId);

  // Cache the local player id whenever the panel opens. Cheap; the auth
  // service has it from session bootstrap.
  useEffect(() => {
    if (isOpen) {
      const userId = authService.getSession()?.user?.id ?? null;
      setSelfPlayerId(userId);
    }
  }, [isOpen, setSelfPlayerId]);

  // Auto-dismiss non-critical errors after a brief delay so the user sees
  // the message but doesn't get stuck.  We never force-close the panel on
  // operational errors (e.g. "some resources left in buffer") — only clear
  // the error text so the player can keep managing the planet.
  useEffect(() => {
    if (!lastError) return;
    const t = setTimeout(() => {
      usePlanetPanelStore.getState().setError(null);
    }, 3000);
    return () => clearTimeout(t);
  }, [lastError]);

  // E-key (and Escape) close the panel.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'e' || e.key === 'E' || e.key === 'Escape') {
        e.preventDefault();
        handleLeave();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div style={overlayStyle}>
      {isLoading && (
        <div style={loadingOverlayStyle}>
          <div style={{ color: '#20a5a6', fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>
            Opening planet panel…
          </div>
        </div>
      )}

      <div style={globeContainerStyle}>
        <PlanetGlobe
          onSelect={(lat, lon, clickedStructureId) => {
            usePlanetPanelStore.getState().setSelection({ lat, lon, clickedStructureId });
            networkManager.requestPlanetScanDetails(lat, lon);
          }}
        />
        <PlanetLayerControls />
        <PlanetResourceLegend />
        <PlanetCenterOutpost />
        {isBaking && (
          <div style={bakingIndicatorStyle}>
            <ScaleLoader color="#20a5a6" height={18} width={3} radius={2} margin={2} />
            <span style={{ marginLeft: 8, color: '#20a5a6', fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>
              Enhancing {resourcesOn ? 'resources' : 'terrain'}…
            </span>
          </div>
        )}
      </div>

      <PlanetInfoPanel onLeave={handleLeave} />
    </div>
  );
}

function handleLeave(): void {
  networkManager.closePlanetPanel();
  // Optimistic close so the ship resumes immediately.
  usePlanetPanelStore.getState().finishClose();
  // Tell GameScene to show the ship again.
  emitGameEvent('closePlanetPanel', undefined);
}

// ==================== STYLES ====================

const overlayStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  zIndex: 150,
  background: 'rgba(7, 12, 22, 0.92)',
  display: 'flex',
  flexDirection: 'row',
  overflow: 'hidden',
  pointerEvents: 'auto',
};

const globeContainerStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  position: 'relative',
  overflow: 'hidden',
};

const bakingIndicatorStyle: React.CSSProperties = {
  position: 'absolute',
  top: 14,
  left: 14,
  display: 'flex',
  alignItems: 'center',
  padding: '6px 12px',
  background: 'rgba(15, 27, 38, 0.85)',
  border: '1px solid rgba(32, 165, 166, 0.3)',
  borderRadius: 6,
  zIndex: 10,
  pointerEvents: 'none',
};

const loadingOverlayStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  zIndex: 200,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(7, 12, 22, 0.92)',
};
